/**
 * Stable integer hashing for everything M2 seeds (plan §2.11): rocks, wrecks, contract boards, price drift and
 * encounters derive their randomness from `hash32(seed, id, salt)` instead of `sim.rng`, so M1 determinism
 * tests keep their sequences and a save only needs seeds plus deltas.
 *
 * MurmurHash3 (x86, 32-bit) over 32-bit words, built on `Math.imul` and unsigned shifts only: identical on
 * every platform and JS engine. Every value is converted with ToUint32 (`>>> 0`), so pass integers
 * (negative integers are fine; fractions are truncated toward the 32-bit pattern and should be avoided).
 * Strings go through `hashString` first (station ids, contract ids, item keys).
 *
 * `hash3` and `seededUnit` are fixed-arity and allocation-free (hot-loop safe, D-014); `hash32(...values)`
 * builds a rest array per call, so keep it out of per-tick code.
 */

const C1 = 0xcc9e2d51
const C2 = 0x1b873593

function rotl(x: number, r: number): number {
  return (x << r) | (x >>> (32 - r))
}

/** Mixes one 32-bit word into the running hash `h` (one MurmurHash3 block). */
export function hashMix(h: number, value: number): number {
  let k = value >>> 0
  k = Math.imul(k, C1)
  k = rotl(k, 15)
  k = Math.imul(k, C2)
  let r = h ^ k
  r = rotl(r, 13)
  return (Math.imul(r, 5) + 0xe6546b64) | 0
}

/** MurmurHash3 finalisation of `h` after `count` words; returns an unsigned 32-bit integer. */
export function hashFinal(h: number, count: number): number {
  let r = h ^ (count * 4)
  r ^= r >>> 16
  r = Math.imul(r, 0x85ebca6b)
  r ^= r >>> 13
  r = Math.imul(r, 0xc2b2ae35)
  r ^= r >>> 16
  return r >>> 0
}

/** Hash of any number of 32-bit integers. Allocates its rest array: not for per-tick code (use `hash3`). */
export function hash32(...values: number[]): number {
  let h = 0
  for (let i = 0; i < values.length; i++) h = hashMix(h, values[i])
  return hashFinal(h, values.length)
}

/** `hash32(a, b, c)` without the rest array (allocation-free). */
export function hash3(a: number, b: number, c: number): number {
  return hashFinal(hashMix(hashMix(hashMix(0, a), b), c), 3)
}

/** A stable 32-bit hash of a string's UTF-16 code units (use it to feed ids like `hub` into `hash32`). */
export function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = hashMix(h, s.charCodeAt(i))
  return hashFinal(h, s.length)
}

/** A float in [0, 1) from `hash3(seed, id, salt)`: the per-id random number of M2 (allocation-free). */
export function seededUnit(seed: number, id: number, salt: number): number {
  return hash3(seed, id, salt) / 4294967296
}
